// pages/hr/wizard/steps/CompletionStep.tsx
import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemContent,
  ListItemDecorator,
  Alert,
} from '@mui/joy';
import {
  Confetti as CelebrationOutlined,
  CheckCircle as CheckCircleOutlined,
  Envelope as EmailOutlined,
  GraduationCap as SchoolOutlined,
  Calendar as EventOutlined,
} from '@phosphor-icons/react';
import type { WizardStepProps } from '../../../../types/hr';

export const CompletionStep: React.FC<WizardStepProps> = ({
  sessionState,
}) => {
  const firstName = sessionState.team_member_name.split(' ')[0];

  return (
    <Box sx={{ p: 4, textAlign: 'center' }}>
      <CelebrationOutlined size={64} color="var(--joy-palette-success-500)" style={{ marginBottom: 16 }} />
      <Typography level="h2" sx={{ mb: 2 }}>
        Congratulations, {firstName}!
      </Typography>
      <Typography level="body-lg" color="neutral" sx={{ mb: 4, maxWidth: 500, mx: 'auto' }}>
        You've successfully completed your onboarding. Your information has been submitted and is being reviewed by our HR team.
      </Typography>

      <Alert color="success" variant="soft" sx={{ mb: 4, mx: 'auto', maxWidth: 500 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircleOutlined />
          <Typography level="body-md" fontWeight="md">
            Your onboarding is complete and awaiting HR approval
          </Typography>
        </Box>
      </Alert>

      <Card variant="outlined" sx={{ maxWidth: 500, mx: 'auto', textAlign: 'left' }}>
        <CardContent>
          <Typography level="title-md" sx={{ mb: 2 }}>
            What happens next?
          </Typography>
          <List>
            <ListItem>
              <ListItemDecorator>
                <EmailOutlined color="var(--joy-palette-primary-500)" />
              </ListItemDecorator>
              <ListItemContent>
                <Typography level="title-sm">Check Your Email</Typography>
                <Typography level="body-sm" color="neutral">
                  You'll receive a confirmation email at {sessionState.team_member_email}
                </Typography>
              </ListItemContent>
            </ListItem>
            <ListItem>
              <ListItemDecorator>
                <CheckCircleOutlined color="var(--joy-palette-primary-500)" />
              </ListItemDecorator>
              <ListItemContent>
                <Typography level="title-sm">HR Review</Typography>
                <Typography level="body-sm" color="neutral">
                  Our HR team will review your information within 1-2 business days
                </Typography>
              </ListItemContent>
            </ListItem>
            <ListItem>
              <ListItemDecorator>
                <SchoolOutlined color="var(--joy-palette-primary-500)" />
              </ListItemDecorator>
              <ListItemContent>
                <Typography level="title-sm">Training Materials</Typography>
                <Typography level="body-sm" color="neutral">
                  Once approved, you'll receive access to training materials and company resources
                </Typography>
              </ListItemContent>
            </ListItem>
            <ListItem>
              <ListItemDecorator>
                <EventOutlined color="var(--joy-palette-primary-500)" />
              </ListItemDecorator>
              <ListItemContent>
                <Typography level="title-sm">First Day</Typography>
                <Typography level="body-sm" color="neutral">
                  Your manager will reach out with details about your first day
                </Typography>
              </ListItemContent>
            </ListItem>
          </List>
        </CardContent>
      </Card>

      <Typography level="body-sm" color="neutral" sx={{ mt: 4 }}>
        Questions? Contact HR at{' '}
        <Typography
          component="a"
          href={`mailto:hr@${sessionState.organization_name.toLowerCase().replace(/\s+/g, '')}.com`}
          color="primary"
        >
          hr@{sessionState.organization_name.toLowerCase().replace(/\s+/g, '')}.com
        </Typography>
      </Typography>
    </Box>
  );
};

export default CompletionStep;
